import { Icon } from '@iconify/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CpfCnpjInput } from '@/components/CpfCnpjInput'
import { ApiClientError } from '@/lib/api/client'
import {
  getScratchToken,
  submitScratchReceipt,
} from '@/lib/api/scratch-campaigns-public.service'
import { runOcrOnFile } from '@/lib/raspadinha-receipt'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'
import styles from './RaspadinhaPublicReceiptStep.module.css'

type RaspadinhaPublicReceiptStepProps = {
  campaign: RaspadinhaPublicCampaign
  onReady: () => void
}

type Phase = 'camera' | 'analyzing' | 'manual_cnpj'

export function RaspadinhaPublicReceiptStep({ campaign, onReady }: RaspadinhaPublicReceiptStepProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const capturedBlobRef = useRef<Blob | null>(null)
  const ocrTextRef = useRef('')

  const [phase, setPhase] = useState<Phase>('camera')
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [manualCnpj, setManualCnpj] = useState('')
  const [isCnpjValid, setIsCnpjValid] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  const startCamera = useCallback(async () => {
    setCameraError(null)
    stopCamera()

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Seu navegador não permite acesso à câmera. Use um celular compatível.')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        try {
          await videoRef.current.play()
        } catch {
          // Alguns ambientes (ex.: testes) não implementam play().
        }
      }
    } catch {
      setCameraError(
        'Não foi possível abrir a câmera. Permita o acesso à câmera e tente novamente.'
      )
    }
  }, [stopCamera])

  useEffect(() => {
    void startCamera()
    return () => {
      stopCamera()
    }
  }, [startCamera, stopCamera])

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const video = videoRef.current
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      return null
    }

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(video, 0, 0)

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92)
    })
  }, [])

  const submitReceipt = useCallback(
    async (image: Blob, ocrText: string, cnpj?: string) => {
      const token = getScratchToken(campaign.slug)
      if (!token) {
        setStatusMessage('Sessão expirada. Volte e faça o cadastro novamente.')
        setPhase('camera')
        return
      }

      setIsSubmitting(true)
      setStatusMessage('Validando cupom...')

      try {
        const result = await submitScratchReceipt(campaign.slug, {
          scratchToken: token,
          image,
          ocrText,
          manualCnpj: cnpj,
        })

        if (result.status === 'ready' || result.status === 'duplicate') {
          stopCamera()
          onReady()
          return
        }

        if (result.status === 'needs_manual_cnpj') {
          stopCamera()
          setPhase('manual_cnpj')
          setStatusMessage(
            'Identificamos as compras, mas não o CNPJ do cupom. Informe o CNPJ impresso no cupom.'
          )
          return
        }

        setPhase('camera')
        setStatusMessage(
          result.message ??
            'Não foi possível ler o cupom. Tire outra foto com melhor iluminação e enquadramento.'
        )
        void startCamera()
      } catch (error) {
        const message =
          error instanceof ApiClientError
            ? error.message
            : 'Não foi possível validar o cupom. Tente novamente.'
        setPhase('camera')
        setStatusMessage(message)
        void startCamera()
      } finally {
        setIsSubmitting(false)
        setProgress(null)
      }
    },
    [campaign.slug, onReady, startCamera, stopCamera]
  )

  const handleCapture = useCallback(async () => {
    if (isSubmitting || phase === 'analyzing') return

    setPhase('analyzing')
    setStatusMessage('Capturando foto...')
    setProgress(0)

    const blob = await captureFrame()
    if (!blob) {
      setPhase('camera')
      setStatusMessage('Não foi possível capturar a foto. Tente novamente.')
      setProgress(null)
      return
    }

    capturedBlobRef.current = blob

    try {
      const ocr = await runOcrOnFile(blob, (message, value) => {
        setStatusMessage(message)
        if (typeof value === 'number') setProgress(value)
      })

      if (!ocr.text.trim()) {
        setPhase('camera')
        setStatusMessage(
          'Não foi possível ler o cupom. Tire outra foto com melhor iluminação e enquadramento.'
        )
        setProgress(null)
        return
      }

      ocrTextRef.current = ocr.text
      await submitReceipt(blob, ocr.text)
    } catch {
      setPhase('camera')
      setStatusMessage(
        'Falha ao analisar o cupom. Tire outra foto com melhor iluminação e enquadramento.'
      )
      setProgress(null)
    }
  }, [captureFrame, isSubmitting, phase, submitReceipt])

  const handleManualSubmit = useCallback(async () => {
    const blob = capturedBlobRef.current
    if (!blob || !isCnpjValid || isSubmitting) return
    await submitReceipt(blob, ocrTextRef.current, manualCnpj)
  }, [isCnpjValid, isSubmitting, manualCnpj, submitReceipt])

  const handleRetake = useCallback(() => {
    capturedBlobRef.current = null
    ocrTextRef.current = ''
    setManualCnpj('')
    setIsCnpjValid(false)
    setStatusMessage(null)
    setPhase('camera')
    void startCamera()
  }, [startCamera])

  if (phase === 'manual_cnpj') {
    return (
      <section className={styles.step} aria-labelledby="receipt-cnpj-title">
        <h1 id="receipt-cnpj-title" className={styles.title}>
          Informe o CNPJ do cupom
        </h1>
        <p className={styles.subtitle}>
          {statusMessage ??
            'Digite o CNPJ da empresa emitente impresso no cupom fiscal para continuar.'}
        </p>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="receipt-manual-cnpj">
            CNPJ
          </label>
          <CpfCnpjInput
            id="receipt-manual-cnpj"
            mode="cnpj"
            value={manualCnpj}
            onChange={(masked) => setManualCnpj(masked)}
            onValidationChange={setIsCnpjValid}
          />
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={!isCnpjValid || isSubmitting}
            onClick={() => void handleManualSubmit()}
          >
            {isSubmitting ? 'Validando...' : 'Continuar'}
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={isSubmitting}
            onClick={handleRetake}
          >
            Tirar outra foto
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className={styles.step} aria-labelledby="receipt-camera-title">
      <h1 id="receipt-camera-title" className={styles.title}>
        Leitura do cupom fiscal
      </h1>
      <p className={styles.subtitle}>
        Enquadre o cupom inteiro, com o QR Code visível, e tire a foto. Não é possível enviar
        arquivos da galeria.
      </p>

      {cameraError ? (
        <p className={styles.errorText} role="alert">
          {cameraError}
        </p>
      ) : (
        <div className={styles.cameraWrap}>
          <video ref={videoRef} className={styles.video} playsInline muted autoPlay />
        </div>
      )}

      {statusMessage ? (
        <p className={styles.status} role="status" aria-live="polite">
          {statusMessage}
        </p>
      ) : null}

      {progress !== null ? (
        <progress className={styles.progress} max={100} value={progress} aria-label="Progresso da leitura" />
      ) : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryButton}
          disabled={Boolean(cameraError) || phase === 'analyzing' || isSubmitting}
          onClick={() => void handleCapture()}
        >
          {phase === 'analyzing' || isSubmitting ? (
            <>
              <Icon icon="mdi:loading" className={styles.spinIcon} aria-hidden />
              Analisando...
            </>
          ) : (
            <>
              <Icon icon="mdi:camera" aria-hidden />
              Tirar foto do cupom
            </>
          )}
        </button>
        {cameraError ? (
          <button type="button" className={styles.secondaryButton} onClick={() => void startCamera()}>
            Tentar abrir câmera novamente
          </button>
        ) : null}
      </div>
    </section>
  )
}
